import { useState, useEffect } from 'react';
import SiteSwitcher from '@/components/SiteSwitcher';
import { useLocation } from 'react-router-dom';
import Link from '@/lib/siteLink';
import { Menu, X, Sparkles, ChevronDown, Sun, Moon, Globe } from 'lucide-react';
import { useGamesLanguage, LANGUAGES } from './useGamesLanguage';
import { t } from './i18n';

const CATEGORIES = [
  { slug: 'cricket', key: 'cricket', label: 'Cricket' },
  { slug: 'gaming', key: 'gaming', label: 'Gaming' },
  { slug: 'esports', key: 'esports', label: 'Esports' },
  { slug: 'soccer', key: 'soccer', label: 'Soccer' },
  { slug: 'entertainment', key: 'entertainment', label: 'Entertainment' },
];

const PILLARS = [
  { id: 'read',   path: '/games',         key: 'read',   hot: false },
  { id: 'shop',   path: '/games/shop',    key: 'shop',   hot: false },
  { id: 'play',   path: '/games/play',    key: 'play',   hot: false },
  { id: 'create', path: '/games/create',  key: 'create', hot: true },
];

export default function GamesNav({ theme = 'dark', toggleTheme = () => {} }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [readOpen, setReadOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { lang, setLang } = useGamesLanguage();
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); setReadOpen(false); setLangOpen(false); }, [location.pathname]);

  const isPillarActive = (pillar) => {
    const p = location.pathname;
    if (pillar.id === 'read') {
      return p === '/games' || CATEGORIES.some(c => p.startsWith(`/games/${c.slug}`)) || p.startsWith('/games/article');
    }
    return p === pillar.path || p.startsWith(pillar.path + '/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-slate-50 dark:bg-[#0F172A]/95 backdrop-blur-md border-b border-slate-200 dark:border-white/5" data-testid="games-navbar">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/games" className="flex items-center gap-2" data-testid="games-logo-link">
          <img src="/games-logo.png" alt="GamesNFans" className="h-11 sm:h-12 w-auto dark:bg-transparent bg-[#0F172A] rounded-lg p-0.5" />
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-1">
          {PILLARS.map((p) => p.id === 'read' ? (
            <div key={p.id} className="relative" onMouseEnter={() => setReadOpen(true)} onMouseLeave={() => setReadOpen(false)}>
              <Link
                to={p.path}
                className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors inline-flex items-center gap-1 ${isPillarActive(p) ? 'text-amber-600 dark:text-yellow-400' : 'text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-white/5'}`}
                data-testid={`nav-pillar-${p.id}`}
              >
                {t(p.key, lang)} <ChevronDown className="w-3 h-3" />
              </Link>
              {readOpen && (
                <div className="absolute top-full left-0 pt-2 w-56">
                  <div className="bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded-xl shadow-xl py-2 max-h-[70vh] overflow-y-auto">
                    <div className="px-4 py-1 text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">{t('byTopic', lang)}</div>
                    {CATEGORIES.map(c => (
                      <Link key={c.slug} to={`/games/${c.slug}`} className="block px-4 py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400 hover:bg-slate-100 dark:bg-white/5" data-testid={`nav-read-${c.slug}`}>{t(c.key, lang)}</Link>
                    ))}
                    <div className="my-1 border-t border-slate-200 dark:border-white/5" />
                    <div className="px-4 py-1 text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">{t('hubs', lang)}</div>
                    <Link to="/games/cricketer" className="block px-4 py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400 hover:bg-slate-100 dark:bg-white/5">{t('cricketerProfiles', lang)}</Link>
                    <Link to="/games/ipl" className="block px-4 py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400 hover:bg-slate-100 dark:bg-white/5">{t('iplHub', lang)}</Link>
                    <Link to="/games/tournament" className="block px-4 py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400 hover:bg-slate-100 dark:bg-white/5">{t('esportsTournaments', lang)}</Link>
                    <Link to="/games/loadout" className="block px-4 py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400 hover:bg-slate-100 dark:bg-white/5">{t('loadouts', lang)}</Link>
                    <Link to="/games/game-guide" className="block px-4 py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400 hover:bg-slate-100 dark:bg-white/5">{t('gameGuides', lang)}</Link>
                    <Link to="/games/guide" className="block px-4 py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400 hover:bg-slate-100 dark:bg-white/5">{t('gearGuides', lang)}</Link>
                    <Link to="/games/watch" className="block px-4 py-2 text-sm text-cyan-600 dark:text-cyan-300 hover:text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-white/5">{t('whereToWatch', lang)}</Link>
                    <Link to="/games/anime" className="block px-4 py-2 text-sm text-pink-600 dark:text-pink-300 hover:text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-white/5">{t('animeGames', lang)}</Link>
                    <Link to="/games/franchise" className="block px-4 py-2 text-sm text-orange-600 dark:text-orange-300 hover:text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-white/5">{t('moviesGames', lang)}</Link>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              key={p.id}
              to={p.path}
              className={`relative px-3 py-2 text-sm font-semibold rounded-lg transition-colors inline-flex items-center gap-1.5 ${isPillarActive(p) ? 'text-amber-600 dark:text-yellow-400' : 'text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-white/5'}`}
              data-testid={`nav-pillar-${p.id}`}
            >
              {p.id === 'create' && <Sparkles className="w-3.5 h-3.5 text-purple-600 dark:text-purple-300" />}
              {t(p.key, lang)}
              {p.hot && <span className="text-[9px] font-black bg-purple-500 text-slate-900 dark:text-white px-1.5 py-0.5 rounded uppercase">{t('new', lang)}</span>}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <div className="relative" onMouseEnter={() => setLangOpen(true)} onMouseLeave={() => setLangOpen(false)}>
            <button
              data-testid="language-toggle-btn"
              aria-label={t('language', lang)}
              title={t('language', lang)}
              className="inline-flex items-center gap-1 p-2 rounded-full border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:scale-105 transition-all duration-300"
            >
              <Globe className="w-4 h-4" />
              <span className="text-[10px] font-bold uppercase hidden sm:inline">{lang}</span>
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 pt-2 w-40" data-testid="language-dropdown">
                <div className="bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded-xl shadow-xl py-2">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      data-testid={`language-option-${l.code}`}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-white/5 ${lang === l.code ? 'text-amber-600 dark:text-yellow-400 font-semibold' : 'text-slate-600 dark:text-gray-300'}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="relative inline-flex items-center p-2 rounded-full border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 transition-all duration-300 hover:scale-105"
          >
            <Sun className={`w-4 h-4 text-amber-500 transition-all duration-300 ${theme === 'dark' ? 'absolute opacity-0 -rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} />
            <Moon className={`w-4 h-4 text-indigo-400 transition-all duration-300 ${theme === 'dark' ? 'opacity-100 rotate-0 scale-100' : 'absolute opacity-0 rotate-90 scale-0'}`} />
          </button>
          <SiteSwitcher className="md:hidden text-slate-600 dark:text-gray-300" />
          <button className="md:hidden p-2 text-slate-600 dark:text-gray-300" onClick={() => setMobileOpen(!mobileOpen)} data-testid="games-mobile-menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-[#0F172A] px-6 py-4 space-y-1" data-testid="games-mobile-panel">
          {PILLARS.map(p => (
            <Link key={p.id} to={p.path} className={`block py-2.5 text-sm font-semibold ${isPillarActive(p) ? 'text-amber-600 dark:text-yellow-400' : 'text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white'}`}>
              {t(p.key, lang)}{p.hot && <span className="ml-2 text-[9px] font-black bg-purple-500 text-slate-900 dark:text-white px-1.5 py-0.5 rounded uppercase">{t('new', lang)}</span>}
            </Link>
          ))}
          <div className="pt-3 mt-3 border-t border-slate-200 dark:border-white/5">
            <div className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-2">{t('byTopic', lang)}</div>
            {CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/games/${c.slug}`} className="block py-2 text-sm text-slate-600 dark:text-gray-300 hover:text-amber-600 dark:text-yellow-400">{t(c.key, lang)}</Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

export { CATEGORIES };
